// Content for the "System Load Order" documentation section.
//
// "System to ClientVerse Core" (systemToClientverseContent.ts) documents the
// cross-system phased plan — Prestep through Step 12 — the order in which
// *different* source systems' data lands relative to each other. This file
// answers a narrower, complementary question: within one source system, what
// order do that system's own staging tables need to load in so every foreign
// key resolves the first time, instead of a table landing before the row it
// points to exists?
//
// Grounded in the same two documents System to ClientVerse Core draws from —
// `_documents/staging-mapping-report.csv` (field-level schema) and
// `_documents/staging-to-salesforce-load-plan.md` — plus the staging table
// field lists already captured in systemToClientverseContent.ts. A dependency
// is marked `confirmed: true` only when the schema states it explicitly
// (literal "FK to stg.X" phrasing, or an explicit "joins back to X.source_id"
// / "joins back to X" description). Where a table's shape strongly implies a
// dependency but no such phrasing was found, it's marked `confirmed: false`
// and called out — never asserted as fact. `CRM_*` tables (the generic-CRM
// equivalents used when a client's CRM isn't InterAction) have no field-level
// schema captured anywhere yet, so no load order is documented for them here.

/** One staging table within a source system's own load order. */
export interface LoadTableRow {
  table: string;
  /** Other tables in the SAME system that must load first, by name. Empty for a root table. */
  dependsOn: string[];
  /** Plain-English reason, naming the actual FK field(s) this ordering is based on. */
  reason: string;
  /** True when the schema states the dependency explicitly (an "FK to stg.X" or "joins back to X" description); false when it's inferred from naming/shape and not yet confirmed. */
  confirmed: boolean;
  /** False for a table that's populated and used internally (e.g. billing analytics) but never sent to ClientVerse Core at all. Defaults to true. */
  loadsToClientVerse?: boolean;
}

/** One load group — tables in the same wave can load in any order relative to each other, but only after every earlier wave has landed. */
export interface LoadWave {
  label: string;
  tables: LoadTableRow[];
}

export interface SourceSystem {
  slug: string;
  name: string;
  shortDescription: string;
  overview: string[];
  waves: LoadWave[];
  /** How this system's load order relates to a different system's — e.g. a cross-system ordering rule documented elsewhere. Not this system's own dependency chain. */
  crossSystemNotes?: string[];
  notes?: string[];
  /** Concrete, unresolved schema questions — surfaced wherever a wave has an unconfirmed dependency. */
  openQuestions?: string[];
}

export const OVERVIEW = {
  title: 'System Load Order',
  purpose: [
    'For each source system, the order its own staging tables need to load in — so a table never lands before the row it points to already exists.',
    'This is a different axis than "System to ClientVerse Core." That plan orders steps across systems (e.g. companies before people, people before matters). This page stays inside one system at a time: within your PMS data alone, or within your CRM data alone, which tables come first.',
    'Tables are grouped into load groups. Every table in a group can load in any order relative to the others in that same group, but only after every earlier group has already landed — the group number is the earliest point a table is safe to load, not a required exact position.',
    'This matters most where more than one system feeds the same downstream entity — a merged Person/identity record built from a PMS\'s attorneys/timekeepers and a CRM\'s contacts, for example. Neither side\'s own load order tells you when the merge itself is safe to run; each system\'s section below flags its own people table and what it depends on, so both sides can be confirmed ready independently.',
  ],
  scopeNote:
    'Only systems with a documented section below are covered. A table with no confirmed foreign key in the underlying schema is marked unconfirmed rather than guessed at — see that system\'s Open Questions.',
};

export const SYSTEMS: SourceSystem[] = [
  {
    slug: 'aderant-pms',
    name: 'Aderant (PMS)',
    shortDescription: 'Practice management: clients, matters, timekeepers, financials.',
    overview: [
      'Aderant is the practice management system (PMS) integration target — the source of record for client status, matters, timekeeper assignments, and billing data. The tables below are the `PMS_*` staging tables.',
      'Nearly every table keys off a `*_source_id` column — the PMS\'s own native id for the row it points to, not a Salesforce id. `PMS_Client` and `PMS_Timekeeper` are the two tables nothing else in this system depends on; everything else joins back to one or both of them, directly or by way of `PMS_Matter`.',
    ],
    waves: [
      {
        label: 'Load group 1 — no dependencies within Aderant',
        tables: [
          { table: 'PMS_Client', dependsOn: [], reason: 'Root table — every client-scoped PMS table joins back to it via client_source_id.', confirmed: true },
          { table: 'PMS_Timekeeper', dependsOn: [], reason: 'Root table — attorneys/paralegals/billable staff, referenced by matter and financial tables via timekeeper_source_id.', confirmed: true },
        ],
      },
      {
        label: 'Load group 2 — depend only on group 1',
        tables: [
          { table: 'PMS_Client_Address', dependsOn: ['PMS_Client'], reason: 'client_source_id joins back to PMS_Client.source_id.', confirmed: true },
          { table: 'PMS_Client_Contact', dependsOn: ['PMS_Client'], reason: 'client_source_id joins back to PMS_Client.source_id.', confirmed: true },
          { table: 'PMS_Related_Company', dependsOn: ['PMS_Client'], reason: 'client_source_id joins back to PMS_Client.source_id. When the related company is itself a PMS client, related_client_source_id links the two client rows together — the same table, so this stays a group-2 dependency.', confirmed: true },
          { table: 'PMS_Matter_Group', dependsOn: ['PMS_Client'], reason: 'client_source_id joins back to PMS_Client.source_id.', confirmed: true },
          { table: 'PMS_Matter', dependsOn: ['PMS_Client'], reason: 'client_source_id joins back to PMS_Client.source_id (confirmed — this is the dependency that gates the load). billing_attorney_source_id and responsible_attorney_source_id also join back to a timekeeper record; the schema describes this as "joins back to the timekeeper record in the source system" without table-qualifying it, but PMS_Timekeeper is the only timekeeper table in this system.', confirmed: true },
        ],
      },
      {
        label: 'Load group 3 — depend on group 2',
        tables: [
          { table: 'PMS_Matter_Party', dependsOn: ['PMS_Matter'], reason: 'matter_source_id joins back to PMS_Matter.source_id.', confirmed: true },
          { table: 'PMS_Matter_Team', dependsOn: ['PMS_Matter', 'PMS_Timekeeper'], reason: 'matter_source_id joins back to PMS_Matter.source_id; timekeeper_source_id joins back to PMS_Timekeeper.source_id.', confirmed: true },
          { table: 'PMS_Financial_Summary', dependsOn: ['PMS_Client', 'PMS_Matter'], reason: 'client_source_id joins back to PMS_Client.source_id (confirmed). matter_source_id is populated only on matter-grained rows (null for client-level rows) and follows the same id pattern as PMS_Matter.source_id elsewhere, but the schema doesn\'t phrase it as an explicit join — treat the matter half as inferred.', confirmed: true },
          { table: 'PMS_Timekeeper_Summary', dependsOn: ['PMS_Timekeeper', 'PMS_Matter'], reason: 'timekeeper_source_id joins back to PMS_Timekeeper.source_id (confirmed). matter_source_id is populated only for matter-scoped rows, following the same pattern — inferred, not an explicit join in the schema text.', confirmed: true },
          { table: 'PMS_TimeEntry', dependsOn: ['PMS_Matter'], reason: 'matter_source_id joins back to PMS_Matter.source_id.', confirmed: true, loadsToClientVerse: false },
        ],
      },
    ],
    crossSystemNotes: [
      'This page covers order within Aderant only. Across systems, Aderant/PMS loads second at the Companies step — the CRM\'s company feed establishes the base Account first, and PMS_Client enriches the same Account using the match Reconcile Client Records (Prestep C) already resolved. See Step 2 of "System to ClientVerse Core."',
      'PMS_Timekeeper is Aderant\'s people table — attorneys, paralegals, and other billable staff. It\'s a load-group-1 root, independent of the client/matter chain, so it can load as soon as the extract is ready. The attorney-role fields elsewhere in this system (billing/responsible/originating attorney on PMS_Client and PMS_Matter, and the role assignments on PMS_Matter_Team) all resolve against it — see Step 3 ("People") of "System to ClientVerse Core" for how Aderant\'s people data merges with a CRM\'s contact data into one identity.',
    ],
    notes: [
      'PMS_TimeEntry is populated and used for internal billing analytics but is never sent to ClientVerse Core — it\'s listed here because PMS_Matter still has to load before it, not because it needs a ClientVerse load slot.',
    ],
  },
  {
    slug: 'interaction-crm',
    name: 'InterAction (CRM)',
    shortDescription: 'Relationship management: companies, contacts, relationships, activities, lists.',
    overview: [
      'InterAction is the CRM integration target covered here. A client on a different CRM uses the generic `CRM_*` staging tables instead — those have no field-level schema captured anywhere yet, so no load order is documented for them; treat that as an open item, not an assumption that they behave like `IA_*`.',
      'InterAction shares one id space (LISTING_ID) between companies and people — `IA_Company` and `IA_Contact` are both "listings." That\'s why so many attachment tables below (phone, email, address, classification, custom fields) use a single polymorphic ia_listing_id column that can point at either a company or a contact row, rather than two separate foreign keys.',
    ],
    waves: [
      {
        label: 'Load group 1 — no dependencies within InterAction',
        tables: [
          { table: 'IA_Company', dependsOn: [], reason: 'Root table — every other IA table anchors back to a company, directly or by way of IA_Contact. Self-referencing via parent_company_id, so a parent company row should load before or together with its children.', confirmed: true },
          { table: 'IA_List', dependsOn: [], reason: 'Root table for InterAction folders/lists. Self-referencing via parent_list_id.', confirmed: true },
        ],
      },
      {
        label: 'Load group 2 — depend only on group 1',
        tables: [
          { table: 'IA_Contact', dependsOn: ['IA_Company'], reason: 'ia_company_id names the contact\'s related/employer company.', confirmed: true },
          { table: 'IA_Company_Field_Value', dependsOn: ['IA_Company'], reason: 'ia_company_id ties the custom-field value to its company row.', confirmed: true },
          { table: 'IA_List_Relationship', dependsOn: ['IA_List'], reason: 'ia_list_id_1 and ia_list_id_2 link two IA_List rows to each other.', confirmed: true },
          { table: 'IA_List_Field', dependsOn: ['IA_List'], reason: 'Folder-scoped custom-field definitions — logically scoped to a list, but no ia_list_id-style column was found among the schema rows checked. Treat this dependency as likely but unconfirmed; see Open Questions.', confirmed: false },
        ],
      },
      {
        label: 'Load group 3 — depend on group 2 (mainly IA_Contact)',
        tables: [
          { table: 'IA_Contact_Field_Value', dependsOn: ['IA_Contact'], reason: 'ia_listing_id ties the custom-field value to its contact row.', confirmed: true },
          { table: 'IA_Phone', dependsOn: ['IA_Company', 'IA_Contact'], reason: 'ia_listing_id is a polymorphic reference into the shared LISTING_ID space — the row can attach to either a company or a contact, so it\'s only safe to load after both.', confirmed: true },
          { table: 'IA_Email', dependsOn: ['IA_Company', 'IA_Contact'], reason: 'Same polymorphic ia_listing_id pattern as IA_Phone.', confirmed: true },
          { table: 'IA_Address', dependsOn: ['IA_Company', 'IA_Contact'], reason: 'Same polymorphic ia_listing_id pattern.', confirmed: true },
          { table: 'IA_Classification', dependsOn: ['IA_Company', 'IA_Contact'], reason: 'Same polymorphic ia_listing_id pattern.', confirmed: true },
          { table: 'IA_Custom_Field', dependsOn: ['IA_Company', 'IA_Contact'], reason: 'Same polymorphic ia_listing_id pattern.', confirmed: true },
          { table: 'IA_Relationship', dependsOn: ['IA_Company', 'IA_Contact'], reason: 'listing_id_1 and listing_id_2 (the two endpoints of the who-knows-whom edge), plus ia_company_id and ia_contact_id, all resolve into the shared LISTING_ID space.', confirmed: true },
          { table: 'IA_Activity', dependsOn: ['IA_Company', 'IA_Contact'], reason: 'ia_company_id and ia_contact_id name the activity\'s related company and contact.', confirmed: true },
          { table: 'IA_Contact_Work_History', dependsOn: ['IA_Contact', 'IA_Company'], reason: 'ia_contact_id names the contact; ia_company_id names their employer at that point in the history.', confirmed: true },
          { table: 'IA_List_Member', dependsOn: ['IA_List', 'IA_Company', 'IA_Contact'], reason: 'ia_list_id names the list; ia_listing_id (polymorphic) and ia_contact_id name the member being added to it.', confirmed: true },
        ],
      },
      {
        label: 'Load group 4 — depend on group 3',
        tables: [
          { table: 'IA_Address_Link', dependsOn: ['IA_Address', 'IA_Company', 'IA_Contact'], reason: 'ia_address_id joins to IA_Address; ia_listing_id (polymorphic) names the owning company or contact.', confirmed: true },
          { table: 'IA_Activity_Attendee', dependsOn: ['IA_Activity', 'IA_Contact'], reason: 'ia_activity_id joins to IA_Activity; ia_contact_id names the attendee.', confirmed: true },
          { table: 'IA_List_Member_Value', dependsOn: ['IA_Contact', 'IA_List_Member'], reason: 'ia_contact_id is confirmed in the schema. A link back to the specific IA_List_Member row (or to IA_List_Field) this value is scoped to is implied by "folder-scoped" but wasn\'t found as an explicit column — treat the IA_List_Member dependency as unconfirmed; see Open Questions.', confirmed: false },
        ],
      },
    ],
    crossSystemNotes: [
      'This page covers order within InterAction only. Across systems, InterAction loads first at the Companies step, establishing the full company universe before PMS_Client enriches the same Account — see Step 2 of "System to ClientVerse Core." Getting that cross-system order backwards (PMS before CRM) risks creating a bare "client-only" Account that the CRM load then has to reconcile against instead of simply enriching.',
      'IA_Contact is InterAction\'s people table — external contacts. It\'s a load-group-2 table, depending only on IA_Company (load group 1), so it doesn\'t wait on the rest of this system\'s tables. A merge step that assembles one Person/identity record from more than one system\'s people data (e.g. a PMS\'s attorneys/timekeepers plus a CRM\'s contacts) isn\'t a table in either system\'s own list — it\'s a step that runs after each contributing system\'s own people table has loaded (here, IA_Contact; on the PMS side, PMS_Timekeeper). See Step 3 ("People") of "System to ClientVerse Core."',
    ],
    openQuestions: [
      'Does IA_List_Field carry an explicit ia_list_id (or equivalent) column tying a custom-field definition to its owning folder? Not confirmed in the schema rows checked.',
      'Does IA_List_Member_Value reference IA_List_Member (or IA_List_Field) by id, beyond the confirmed ia_contact_id? Assumed but not confirmed — needed before this dependency can move from Group 4 to a verified position.',
      'IA_Company_Field and IA_Contact_Field (the custom-field *definition* tables, distinct from the *_Value tables above) weren\'t found with their own confirmed FK columns in this pass — confirm their shape before documenting a load position for them.',
      'None of the generic CRM_* tables (CRM_Company, CRM_Contact, CRM_List, CRM_Address, CRM_Activity, CRM_Opportunity, CRM_User, CRM_Relationship) have field-level schema captured anywhere yet. A client on a non-InterAction CRM needs that captured before a load order can be documented for them — don\'t assume it mirrors IA_* one-for-one.',
    ],
  },
];

export function systemBySlug(slug: string | undefined): SourceSystem | undefined {
  return SYSTEMS.find((s) => s.slug === slug);
}

// ---------------------------------------------------------------------------
// Markdown export
// ---------------------------------------------------------------------------
export function systemToMarkdown(system: SourceSystem): string {
  const lines: string[] = [];
  lines.push(`# System Load Order — ${system.name}`);
  lines.push('');
  for (const p of system.overview) {
    lines.push(p);
    lines.push('');
  }
  for (const wave of system.waves) {
    lines.push(`## ${wave.label}`);
    lines.push('');
    lines.push('| Table | Depends on | Reason | To ClientVerse |');
    lines.push('|---|---|---|---|');
    for (const row of wave.tables) {
      const dependsOn = row.dependsOn.length === 0 ? '*(none)*' : row.dependsOn.join(', ');
      const unconfirmed = row.confirmed ? '' : '**[UNCONFIRMED]** ';
      const toCv = row.loadsToClientVerse === false ? 'No' : 'Yes';
      lines.push(`| ${row.table} | ${dependsOn} | ${unconfirmed}${row.reason} | ${toCv} |`);
    }
    lines.push('');
  }
  if (system.crossSystemNotes && system.crossSystemNotes.length > 0) {
    lines.push('## Relationship to other systems\' load order');
    lines.push('');
    for (const n of system.crossSystemNotes) {
      lines.push(n);
      lines.push('');
    }
  }
  if (system.openQuestions && system.openQuestions.length > 0) {
    lines.push('## Open questions for the ClientVerse architect');
    lines.push('');
    system.openQuestions.forEach((q, i) => lines.push(`${i + 1}. ${q}`));
    lines.push('');
  }
  for (const n of system.notes ?? []) {
    lines.push(n);
    lines.push('');
  }
  return lines.join('\n');
}

export function landingToMarkdown(): string {
  const lines: string[] = [];
  lines.push(`# ${OVERVIEW.title}`);
  lines.push('');
  for (const p of OVERVIEW.purpose) {
    lines.push(p);
    lines.push('');
  }
  lines.push(`**Scope note.** ${OVERVIEW.scopeNote}`);
  lines.push('');
  lines.push('## Systems');
  lines.push('');
  lines.push('| System | Covers |');
  lines.push('|---|---|');
  for (const s of SYSTEMS) {
    lines.push(`| ${s.name} | ${s.shortDescription} |`);
  }
  lines.push('');
  return lines.join('\n');
}
